import {
  GCOOKIE_NAME,
  META_COOKIE,
  KCOOKIE_NAME,
  LCOOKIE_NAME,
  BLOCK_REQUEST_COOKIE,
  ISOLATE_COOKIE,
  MUTE_EXPIRY_KEY
} from './constants'
import encryption from '../modules/security/Encryption'

export class StorageManager {
  static save (key, value) {
    if (!key || !value) {
      return false
    }
    if (this._isLocalStorageSupported()) {
      if (encryption.shouldEncrypt(key)) {
        localStorage.setItem(key, encryption.encrypt(value))
        return true
      }
      localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value))
      return true
    }
  }

  static read (key) {
    if (!key) {
      return false
    }
    let data = null
    if (this._isLocalStorageSupported()) {
      data = localStorage.getItem(key)
    }
    if (data != null) {
      try {
        if (encryption.shouldDecrypt(key)) {
          data = encryption.decrypt(data)
        }
        data = JSON.parse(data)
      } catch (e) {}
    }
    return data
  }

  static remove (key) {
    if (!key) {
      return false
    }
    if (this._isLocalStorageSupported()) {
      localStorage.removeItem(key)
      return true
    }
  }

  static removeCookie (name, domain) {
    let cookieStr = name + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT;'

    if (domain) {
      cookieStr = cookieStr + ' domain=' + domain + '; path=/'
    }

    document.cookie = cookieStr
  }

  static createCookie (name, value, seconds, domain) {
    let expires = ''
    let domainStr = ''
    if (seconds) {
      const date = new Date()
      date.setTime(date.getTime() + (seconds * 1000))

      expires = '; expires=' + date.toGMTString()
    }

    if (domain) {
      domainStr = '; domain=' + domain
    }

    value = encodeURIComponent(value)

    document.cookie = name + '=' + value + expires + domainStr + '; path=/'
  }

  static readCookie (name) {
    const nameEQ = name + '='
    const ca = document.cookie.split(';')
    for (let idx = 0; idx < ca.length; idx++) {
      let c = ca[idx]
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length)
      }
      // eslint-disable-next-line eqeqeq
      if (c.indexOf(nameEQ) == 0) {
        try {
          return decodeURIComponent(c.substring(nameEQ.length, c.length))
        } catch (e) {
          return null
        }
      }
    }
    return null
  }

  static _isLocalStorageSupported () {
    return 'localStorage' in window && window.localStorage !== null && typeof window.localStorage.setItem === 'function'
  }

  static saveToLSorCookie (property, value) {
    if (value == null) {
      return
    }
    try {
      if (this._isLocalStorageSupported()) {
        this.save(property, encodeURIComponent(JSON.stringify(value)))
      } else {
        if (property === GCOOKIE_NAME) {
          this.createCookie(property, encodeURIComponent(value), 0, window.location.hostname)
        } else {
          this.createCookie(property, encodeURIComponent(JSON.stringify(value)), 0, window.location.hostname)
        }
      }
      $ct.globalCache[property] = value
    } catch (e) {}
  }

  static readFromLSorCookie (property) {
    let data
    if ($ct.globalCache.hasOwnProperty(property)) {
      return $ct.globalCache[property]
    }
    if (this._isLocalStorageSupported()) {
      data = this.read(property)
    } else {
      data = this.readCookie(property)
    }

    if (data !== null && data !== undefined && !(typeof data.trim === 'function' && data.trim() === '')) {
      let value
      try {
        value = JSON.parse(decodeURIComponent(data))
      } catch (err) {
        value = decodeURIComponent(data)
      }
      $ct.globalCache[property] = value
      return value
    }
  }

  static createBroadCookie (name, value, seconds, domain, domainSpecification = null) {
    if (domainSpecification) {
      const hostnameParts = window.location.hostname.split('.')
      const level = domainSpecification
      let calculatedDomain = ''
      if (level <= hostnameParts.length) {
        const domainParts = hostnameParts.slice(-level)
        calculatedDomain = '.' + domainParts.join('.')
      } else {
        // If level is greater than available parts, use the full hostname
        calculatedDomain = '.' + window.location.hostname
      }
      let cookieValue = value
      if (name === GCOOKIE_NAME && this.readCookie(name)) {
        // remove duplicate cookies if they exist
        // removing .bank.in because it is a protected domain
        cookieValue = this.readCookie(name)
        this.removeCookie(name, $ct.broadDomain)
        this.removeCookie(name, calculatedDomain)
        this.removeCookie(name, '.bank.in')
      }
      this.createCookie(name, cookieValue, seconds, calculatedDomain)
      return
    }
    /* -------------------------------------------------------------
     * Sub-domain isolation: when the global flag is set, skip the
     * broad-domain logic and write a cookie scoped to the current
     * host only.  Also remove any legacy broad-domain copy so that
     * the host-level cookie has precedence.
     * ----------------------------------------------------------- */
    const isolate = !!this.readFromLSorCookie(ISOLATE_COOKIE)
    if (isolate) {
      // remove any legacy broad-domain cookie
      if ($ct.broadDomain) {
        this.removeCookie(name, $ct.broadDomain)
      }

      // write host-scoped cookie and stop
      this.createCookie(name, value, seconds, domain)
      return
    }
    // sets cookie on the base domain. e.g. if domain is baz.foo.bar.com, set cookie on ".bar.com"
    // To update an existing "broad domain" cookie, we need to know what domain it was actually set on.
    // since a retrieved cookie never tells which domain it was set on, we need to set another test cookie
    // to find out which "broadest" domain the cookie was set on. Then delete the test cookie, and use that domain
    // for updating the actual cookie.

    if (domain) {
      let broadDomain = $ct.broadDomain
      if (broadDomain == null) { // if we don't know the broadDomain yet, then find out
        const domainParts = domain.split('.')
        let testBroadDomain = ''
        for (let idx = domainParts.length - 1; idx >= 0; idx--) {
          if (idx === 0) {
            testBroadDomain = domainParts[idx] + testBroadDomain
          } else {
            testBroadDomain = '.' + domainParts[idx] + testBroadDomain
          }

          // only needed if the cookie already exists and needs to be updated. See note above.
          if (this.readCookie(name)) {
            // no guarantee that browser will delete cookie, hence create short lived cookies
            var testCookieName = 'test_' + name + idx
            this.createCookie(testCookieName, value, 10, testBroadDomain) // self-destruct after 10 seconds
            if (!this.readCookie(testCookieName)) { // if test cookie not set, then the actual cookie wouldn't have been set on this domain either.
              continue
            } else { // else if cookie set, then delete the test and the original cookie
              this.removeCookie(testCookieName, testBroadDomain)
            }
          }

          this.createCookie(name, value, seconds, testBroadDomain)
          const tempCookie = this.readCookie(name)
          // eslint-disable-next-line eqeqeq
          if (tempCookie == value) {
            broadDomain = testBroadDomain
            $ct.broadDomain = broadDomain
            break
          }
        }
      } else {
        this.createCookie(name, value, seconds, broadDomain)
      }
    } else {
      this.createCookie(name, value, seconds, domain)
    }
  }

  static getMetaProp (property) {
    const metaObj = this.readFromLSorCookie(META_COOKIE)
    if (metaObj != null) {
      return metaObj[property]
    }
  }

  static setMetaProp (property, value) {
    if (this._isLocalStorageSupported()) {
      let wzrkMetaObj = this.readFromLSorCookie(META_COOKIE)
      if (wzrkMetaObj == null) {
        wzrkMetaObj = {}
      }
      if (value === undefined) {
        delete wzrkMetaObj[property]
      } else {
        wzrkMetaObj[property] = value
      }
      this.saveToLSorCookie(META_COOKIE, wzrkMetaObj)
    }
  }

  static getAndClearMetaProp (property) {
    const value = this.getMetaProp(property)
    this.setMetaProp(property, undefined)
    return value
  }

  static setInstantDeleteFlagInK () {
    let k = this.readFromLSorCookie(KCOOKIE_NAME)
    if (k == null) {
      k = {}
    }
    k.flag = true
    this.saveToLSorCookie(KCOOKIE_NAME, k)
  }

  static backupEvent (data, reqNo, logger) {
    let backupArr = this.readFromLSorCookie(LCOOKIE_NAME)
    if (typeof backupArr === 'undefined') {
      backupArr = {}
    }
    backupArr[reqNo] = { q: data }
    this.saveToLSorCookie(LCOOKIE_NAME, backupArr)
    logger.debug(`stored in ${LCOOKIE_NAME} reqNo : ${reqNo} -> ${data}`)
  }

  // Add new method for OUL tracking
  static markBackupAsOUL (reqNo) {
    // Store OUL request numbers in a separate meta property
    const oulRequests = this.getMetaProp('OUL_REQUESTS') || []
    if (!oulRequests.includes(reqNo)) {
      oulRequests.push(reqNo)
      this.setMetaProp('OUL_REQUESTS', oulRequests)
    }
  }

  static isBackupOUL (reqNo) {
    const oulRequests = this.getMetaProp('OUL_REQUESTS') || []
    return oulRequests.includes(reqNo)
  }

  static removeBackup (respNo, logger) {
    const backupMap = this.readFromLSorCookie(LCOOKIE_NAME)
    if (typeof backupMap !== 'undefined' && backupMap !== null && typeof backupMap[respNo] !== 'undefined') {
      logger.debug(`del event: ${respNo} data-> ${backupMap[respNo].q}`)
      delete backupMap[respNo]
      this.saveToLSorCookie(LCOOKIE_NAME, backupMap)
    }
  }
}

/**
 * Check if SDK is currently muted (for churned accounts)
 * @returns {boolean} true if SDK is muted and should not send requests
 */
export const isMuted = () => {
  const muteExpiry = StorageManager.readFromLSorCookie(MUTE_EXPIRY_KEY)
  if (!muteExpiry || muteExpiry <= 0) {
    return false
  }
  return Date.now() < muteExpiry
}

/**
 * Get the mute expiry timestamp
 * @returns {number|null} epoch timestamp in ms, or null if not muted
 */
export const getMuteExpiry = () => {
  const muteExpiry = StorageManager.readFromLSorCookie(MUTE_EXPIRY_KEY)
  if (!muteExpiry || muteExpiry <= 0) {
    return null
  }
  return Date.now() < muteExpiry ? muteExpiry : null
}

export const $ct = {
  globalCache: {
    gcookie: null,
    REQ_N: 0,
    RESP_N: 0
  },
  LRU_CACHE: null,
  globalProfileMap: undefined,
  globalEventsMap: undefined,
  // Initialize blockRequest from storage
  get blockRequest () {
    const value = StorageManager.readFromLSorCookie(BLOCK_REQUEST_COOKIE)
    return value === true
  },
  set blockRequest (value) {
    StorageManager.saveToLSorCookie(BLOCK_REQUEST_COOKIE, value)
  },
  isOptInRequest: false,
  broadDomain: null,
  webPushEnabled: null,
  campaignDivMap: {},
  currentSessionId: null,
  wiz_counter: 0, // to keep track of number of times we load the body
  notifApi: {
    notifEnabledFromApi: false
  }, // helper variable to handle race condition and check when notifications were called
  unsubGroups: [],
  updatedCategoryLong: null,
  inbox: null,
  isPrivacyArrPushed: false,
  privacyArray: [],
  offline: false,
  location: null,
  dismissSpamControl: true,
  globalUnsubscribe: true,
  flutterVersion: null,
  variableStore: {},
  pushConfig: null,
  delayEvents: false,
  intervalArray: [],
  enableTVNavigation: false
  // domain: window.location.hostname, url -> getHostName()
  // gcookie: -> device
}
